import React, { useState } from 'react';
import { motion, useAnimation, Variants } from 'framer-motion';
import { Link } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import toast from 'react-hot-toast';
import InstallBanner from '../pwa/InstallBanner';
import { siteConfig, trackPhoneClick, trackEmailClick } from '../../config/site';

const Footer: React.FC = () => {
  const currentYear = new Date().getFullYear();
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState<string>();
  const [touched, setTouched] = useState(false);

  const controls = useAnimation();
  const [ref, inView] = useInView({
    triggerOnce: true,
    threshold: 0.1,
  });

  React.useEffect(() => {
    if (inView) {
      controls.start('visible');
    }
  }, [controls, inView]);

  const containerVariants: Variants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1,
        delayChildren: 0.2,
      }
    }
  };

  const itemVariants: Variants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        type: 'spring',
        stiffness: 300,
        damping: 30
      }
    }
  };

  const validateEmail = (value: string): string | undefined => {
    if (!value.trim()) return 'Email is required';
    const emailRegex = /^\w+([.-]?\w+)*@\w+([.-]?\w+)*(\.\w{2,3})+$/;
    if (!emailRegex.test(value)) return 'Please enter a valid email address';
    return undefined;
  };

  const handleEmailChange = (value: string) => {
    setEmail(value);
    if (touched) {
      setEmailError(validateEmail(value));
    }
  };

  const handleEmailBlur = () => {
    setTouched(true);
    setEmailError(validateEmail(email));
  };

  const handleSubscribe = async (e: React.FormEvent) => {
    e.preventDefault();

    setTouched(true);
    const error = validateEmail(email);
    setEmailError(error);

    if (error) {
      toast.error('Please enter a valid email address to subscribe', {
        duration: 4000,
      });
      return;
    }

    // Simulate API call with better feedback
    const subscribePromise = new Promise((resolve, reject) => {
      setTimeout(() => {
        // Simulate success - in production this would be an actual API call
        const success = Math.random() > 0.1; // 90% success rate for demo
        if (success) {
          resolve(true);
    } else {
          reject(new Error('Failed'));
        }
      }, 1500);
    });

    toast.promise(
      subscribePromise,
      {
        loading: 'Subscribing you to our newsletter...',
        success: () => {
          setEmail('');
          setEmailError(undefined);
          setTouched(false);
          return '🎉 Welcome! Check your email for a confirmation link.';
        },
        error: 'Unable to subscribe at this time. Please try again later.',
      },
      {
        success: {
          duration: 6000,
        },
        error: {
          duration: 5000,
        },
      }
    );
  };

  return (
    <>
      <motion.footer 
        ref={ref}
        className="footer-container"
        initial="hidden"
        animate={controls}
        variants={containerVariants}
        style={{
          backgroundColor: '#f8f9fa',
          backgroundImage: 'linear-gradient(135deg, #f8f9fa 0%, #e9ecef 100%)',
          color: '#343a40',
          position: 'relative',
          overflow: 'hidden'
        }}
      >
        {/* Background gradient accents */}
        <div style={{
          position: 'absolute',
          top: '-150px',
          left: '-150px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(17, 85, 161, 0.07) 0%, rgba(17, 85, 161, 0) 70%)',
          zIndex: 0
        }} />
        
        <div style={{
          position: 'absolute',
          bottom: '-100px',
          right: '-100px',
          width: '300px',
          height: '300px',
          borderRadius: '50%',
          background: 'radial-gradient(circle, rgba(17, 85, 161, 0.05) 0%, rgba(17, 85, 161, 0) 70%)',
          zIndex: 0
        }} />
        
        {/* Main footer content */}
        <div className="footer-main" style={{
          maxWidth: '1400px',
          margin: '0 auto',
          padding: '60px 5vw 40px',
          position: 'relative',
          zIndex: 1
        }}>
          <div className="footer-grid" style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))',
            gap: '30px',
            marginBottom: '40px'
          }}>
            {/* Company Information */}
            <motion.div variants={itemVariants} className="footer-column">
              <div className="footer-logo" style={{ marginBottom: '20px' }}>
                <img 
                  src="/img/logo.svg" 
                  alt="NLC Group UK" 
                  style={{ 
                    width: '140px', 
                    height: 'auto',
                    maxWidth: '100%'
                  }} 
                />
              </div>
              
              <p style={{ fontSize: '15px', lineHeight: 1.6, marginBottom: '20px', color: '#495057' }}>
                NLC Group (Commercial & Industrial) & NLC Construction (Residential) are leading property development experts with over two decades of experience delivering high-quality projects across Yorkshire.
              </p>
              
              {/* Trust Badges */}
              <motion.div 
                variants={itemVariants}
                className="trust-badges"
                style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px',
                  marginTop: '15px'
                }}
              >
                {['Trusted', 'Quality Assured', 'Certified', 'Eco-Friendly'].map((badge, index) => (
                  <div key={index} style={{
                    padding: '4px 10px',
                    borderRadius: '4px',
                    fontSize: '12px',
                    fontWeight: 500,
                    backgroundColor: 'rgba(17, 85, 161, 0.08)',
                    color: 'var(--brand-color)'
                  }}>
                    {badge}
                  </div>
                ))}
              </motion.div>
            </motion.div>
            
            {/* Quick Links */}
            <motion.div variants={itemVariants} className="footer-column">
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '20px',
                position: 'relative',
                paddingBottom: '10px'
              }}>
                Quick Links
                <span style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '40px',
                  height: '3px',
                  backgroundColor: 'var(--brand-color)',
                  borderRadius: '2px'
                }}></span>
              </h3>
              
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  { label: 'Home', path: '/' },
                  { label: 'Services', path: '/services' },
                  { label: 'Gallery', path: '/gallery' },
                  { label: 'Kitchens & Bathrooms', path: '/services/kitchens-bathrooms' },
                  { label: 'Extensions', path: '/services/extensions' },
                  { label: 'Testimonials', path: '/testimonials' },
                  { label: 'Contact Us', path: '/contact' },
                  { label: 'Privacy Policy', path: '/privacy-policy' },
                  { label: 'Terms of Service', path: '/terms-of-service' },
                  { label: 'Cookie Policy', path: '/cookie-policy' }
                ].map((link, index) => (
                  <motion.li 
                    key={index} 
                    style={{ marginBottom: '10px' }}
                    whileHover={{ x: 5 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                  >
                    <Link 
                      to={link.path}
                      style={{
                        textDecoration: 'none',
                        color: '#495057',
                        fontSize: '15px',
                        display: 'flex',
                        alignItems: 'center',
                        gap: '6px',
                        transition: 'color 0.2s ease'
                      }}
                      onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-color)'}
                      onMouseLeave={(e) => e.currentTarget.style.color = '#495057'}
                    >
                      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <polyline points="9 18 15 12 9 6"></polyline>
                      </svg>
                      {link.label}
                    </Link>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            
            {/* Contact Information */}
            <motion.div variants={itemVariants} className="footer-column">
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '20px',
                position: 'relative',
                paddingBottom: '10px'
              }}>
                Contact Us
                <span style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '40px',
                  height: '3px',
                  backgroundColor: 'var(--brand-color)',
                  borderRadius: '2px'
                }}></span>
              </h3>
              
              <ul style={{
                listStyle: 'none',
                padding: 0,
                margin: 0
              }}>
                {[
                  {
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 16.92v3a2 2 0 0 1-2.18 2 19.79 19.79 0 0 1-8.63-3.07 19.5 19.5 0 0 1-6-6 19.79 19.79 0 0 1-3.07-8.67A2 2 0 0 1 4.11 2h3a2 2 0 0 1 2 1.72 12.84 12.84 0 0 0 .7 2.81 2 2 0 0 1-.45 2.11L8.09 9.91a16 16 0 0 0 6 6l1.27-1.27a2 2 0 0 1 2.11-.45 12.84 12.84 0 0 0 2.81.7A2 2 0 0 1 22 16.92z"></path>
                      </svg>
                    ),
                    label: 'Phone',
                    value: siteConfig.phone,
                    href: `tel:${siteConfig.phone.replace(/\s/g, '')}`,
                    onClick: trackPhoneClick
                  },
                  {
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M4 4h16c1.1 0 2 .9 2 2v12c0 1.1-.9 2-2 2H4c-1.1 0-2-.9-2-2V6c0-1.1.9-2 2-2z"></path>
                        <polyline points="22,6 12,13 2,6"></polyline>
                      </svg>
                    ),
                    label: 'Email',
                    value: siteConfig.email,
                    href: `mailto:${siteConfig.email}`,
                    onClick: trackEmailClick
                  },
                  { 
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z"></path>
                        <circle cx="12" cy="10" r="3"></circle>
                      </svg>
                    ), 
                    label: 'Address', 
                    value: 'Leeds, West Yorkshire, UK',
                    href: 'https://goo.gl/maps/wXFBrC2FXd7MxTzt5'
                  },
                  { 
                    icon: (
                      <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <circle cx="12" cy="12" r="10"></circle>
                        <polyline points="12 6 12 12 16 14"></polyline>
                      </svg>
                    ), 
                    label: 'Working Hours', 
                    value: 'Mon-Fri: 8am-6pm, Sat: 9am-2pm',
                    href: '#'
                  }
                ].map((item, index) => (
                  <motion.li 
                    key={index} 
                    style={{ 
                      marginBottom: '16px',
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '12px' 
                    }}
                    whileHover={{ x: 5 }}
                    transition={{ type: 'spring', stiffness: 400 }}
                  >
                    <div style={{
                      minWidth: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      backgroundColor: 'rgba(17, 85, 161, 0.08)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center'
                    }}>
                      {item.icon}
                    </div>
                    
                    <div>
                      <p style={{ 
                        margin: '0 0 3px 0',
                        fontSize: '14px',
                        fontWeight: 600,
                        color: '#343a40'
                      }}>
                        {item.label}
                      </p>
                      <a
                        href={item.href}
                        target={item.href.startsWith('http') ? '_blank' : undefined}
                        rel={item.href.startsWith('http') ? 'noopener noreferrer' : undefined}
                        onClick={item.onClick}
                        style={{
                          textDecoration: 'none',
                          color: '#495057',
                          fontSize: '15px',
                          transition: 'color 0.2s ease'
                        }}
                        onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-color)'}
                        onMouseLeave={(e) => e.currentTarget.style.color = '#495057'}
                      >
                        {item.value}
                      </a>
                    </div>
                  </motion.li>
                ))}
              </ul>
            </motion.div>
            
            {/* Newsletter Signup */}
            <motion.div variants={itemVariants} className="footer-column">
              <h3 style={{
                fontSize: '18px',
                fontWeight: 600,
                marginBottom: '20px',
                position: 'relative',
                paddingBottom: '10px'
              }}>
                Newsletter
                <span style={{
                  position: 'absolute',
                  bottom: 0,
                  left: 0,
                  width: '40px',
                  height: '3px',
                  backgroundColor: 'var(--brand-color)',
                  borderRadius: '2px'
                }}></span>
              </h3>
              
              <p style={{ fontSize: '15px', lineHeight: 1.6, marginBottom: '20px', color: '#495057' }}>
                Subscribe to our newsletter to receive the latest updates, promotions, and inspiration for your home.
              </p>
              
              <form onSubmit={handleSubscribe}>
                <div style={{ 
                  position: 'relative',
                  marginBottom: '15px'
                }}>
                  <input
                    type="email"
                    value={email}
                    onChange={(e) => handleEmailChange(e.target.value)}
                    onBlur={handleEmailBlur}
                    placeholder="Your email address"
                    style={{
                      width: '100%',
                      padding: '12px 15px',
                      paddingRight: '50px',
                      fontSize: '15px',
                      border: touched && emailError ? '1px solid #dc3545' : '1px solid #ced4da',
                      borderRadius: '8px',
                      backgroundColor: 'white',
                      boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                      outline: 'none',
                      transition: 'all 0.3s ease'
                    }}
                    onFocus={(e) => {
                      if (!touched || !emailError) {
                        e.target.style.borderColor = 'var(--brand-color)';
                      }
                    }}
                  />
                  <motion.button
                    type="submit"
                    whileHover={{ scale: 1.1 }}
                    whileTap={{ scale: 0.9 }}
                    style={{
                      position: 'absolute',
                      right: '8px',
                      top: '50%',
                      transform: 'translateY(-50%)',
                      backgroundColor: 'var(--brand-color)',
                      color: 'white',
                      border: 'none',
                      width: '36px',
                      height: '36px',
                      borderRadius: '50%',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      cursor: 'pointer',
                      boxShadow: '0 2px 8px rgba(17, 85, 161, 0.3)'
                    }}
                  >
                    <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <line x1="22" y1="2" x2="11" y2="13"></line>
                      <polygon points="22 2 15 22 11 13 2 9 22 2"></polygon>
                    </svg>
                  </motion.button>
                </div>
                
                {/* Email validation error */}
                {touched && emailError && (
                  <motion.div
                    initial={{ opacity: 0, y: -5 }}
                    animate={{ opacity: 1, y: 0 }}
                    style={{
                      color: '#dc3545',
                      fontSize: '13px',
                      marginTop: '-10px',
                      marginBottom: '10px'
                    }}
                  >
                    {emailError}
                  </motion.div>
                )}
              </form>
              
              {/* Certifications section */}
              <div>
                <h4 style={{
                  fontSize: '16px',
                  fontWeight: 600,
                  marginTop: '25px',
                  marginBottom: '15px'
                }}>
                  Certifications
                </h4>
                
                <div style={{
                  display: 'flex',
                  flexWrap: 'wrap',
                  gap: '10px'
                }}>
                  {[
                    'Certified Contractor',
                    'ISO 9001',
                    'Trading Standards',
                    'Safety Assured'
                  ].map((cert, index) => (
                    <motion.div
                      key={index}
                      whileHover={{ scale: 1.05 }}
                      style={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: '5px',
                        padding: '5px 10px',
                        backgroundColor: 'white',
                        borderRadius: '5px',
                        boxShadow: '0 2px 5px rgba(0,0,0,0.05)',
                        border: '1px solid #ced4da',
                        fontSize: '13px'
                      }}
                    >
                      <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="var(--brand-color)" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M22 11.08V12a10 10 0 1 1-5.93-9.14"></path>
                        <polyline points="22 4 12 14.01 9 11.01"></polyline>
                      </svg>
                      {cert}
                    </motion.div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
          
          {/* Separator */}
          <motion.div
            variants={itemVariants}
            style={{
              height: '1px',
              background: 'linear-gradient(to right, transparent, rgba(0,0,0,0.1), transparent)',
              margin: '20px 0 30px'
            }}
          />
          
          {/* Copyright and bottom footer */}
          <motion.div 
            variants={itemVariants}
            className="footer-bottom"
            style={{
              display: 'flex',
              flexWrap: 'wrap',
              justifyContent: 'space-between',
              alignItems: 'center',
              gap: '20px'
            }}
          >
            <div style={{ fontSize: '14px', color: '#6c757d' }}>
              &copy; {currentYear} NLC Group UK. All rights reserved.
            </div>
            
            <div style={{ display: 'flex', gap: '15px', flexWrap: 'wrap' }}>
              {[
                { name: 'Privacy Policy', path: '/privacy-policy' },
                { name: 'Terms of Service', path: '/terms-of-service' },
                { name: 'Cookie Policy', path: '/cookie-policy' },
                { name: 'Sitemap', path: '/sitemap' }
              ].map((item, index) => (
                <Link
                  key={index}
                  to={item.path}
                  style={{
                    textDecoration: 'none',
                    color: '#6c757d',
                    fontSize: '14px',
                    transition: 'color 0.2s ease'
                  }}
                  onMouseEnter={(e) => e.currentTarget.style.color = 'var(--brand-color)'}
                  onMouseLeave={(e) => e.currentTarget.style.color = '#6c757d'}
                >
                  {item.name}
                </Link>
              ))}
            </div>
            
            <div style={{ fontSize: '14px', color: '#6c757d' }}>
              Developed by <a 
                href="https://heskethwebdesign.co.uk/?ref=nlc-group-uk" 
                target="_blank" 
                rel="noreferrer"
                style={{
                  color: 'var(--brand-color)',
                  textDecoration: 'none',
                  fontWeight: 500
                }}
              >
                Hesketh Web Design
              </a>
            </div>
          </motion.div>
        </div>
      </motion.footer>
      <InstallBanner />
    </>
  );
};

export default Footer; 